import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../ui/breadcrumb';
import { ArrowLeft, Maximize2 } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Shows current scope (collection/category/mounting) with breadcrumbs and expand action
 */
export const ScopeHeader = ({
  scopeType,
  scopeValue,
  scopeLabel,
  onExpandScope,
  resultCount,
  countUnit = 'families',
  error,
  loading,
  hasIntro = false
}) => {
  const getBreadcrumbs = () => {
    const items = [
      { label: 'Home', to: '/' },
      { label: 'Products', to: '/products' }
    ];
    
    if (scopeType === 'collection') {
      items.push(
        { label: 'Collections', to: '/products' },
        { label: scopeLabel, to: `/collections/${scopeValue}` },
        { label: 'Products', current: true }
      );
    } else if (scopeType === 'category') {
      items.push(
        { label: `Category: ${scopeLabel}`, current: true }
      );
    } else if (scopeType === 'mounting') {
      items.push(
        { label: `Mounting: ${scopeLabel}`, current: true }
      );
    } else if (scopeType === 'application') {
      items.push(
        { label: `Application: ${scopeLabel}`, current: true }
      );
    } else {
      items.push({ label: 'All Products', current: true });
    }
    
    return items;
  };

  return (
    <div className="border-b bg-background" data-testid="scope-header">
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-4">
        {/* Breadcrumbs */}
        <Breadcrumb className="mb-3">
          <BreadcrumbList>
            {getBreadcrumbs().map((item, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <BreadcrumbSeparator />}
                <BreadcrumbItem>
                  {item.current ? (
                    <BreadcrumbPage>{item.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link data-testid={`catalog-breadcrumb-${idx}`} to={item.to}>{item.label}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>

        {/* Title and Actions */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap min-w-0 items-center gap-3">
            <h2 data-testid="catalog-results-heading" className={hasIntro ? 'sr-only' : 'text-base md:text-lg break-words max-w-full'}>
              {scopeType === 'global' ? (
                <>
                  <span className="thin">ALL</span> <span className="bold">PRODUCTS</span>
                </>
              ) : (
                <span className="bold">{scopeLabel}</span>
              )}
            </h2>
            
            {/* Scope badge */}
            {scopeType !== 'global' && (
              <Badge 
                variant="outline" 
                className="text-xs uppercase"
                data-testid="scope-badge"
              >
                {scopeType}: {scopeValue}
              </Badge>
            )}
          </div>

          {/* Expand scope action */}
          {scopeType !== 'global' && onExpandScope && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onExpandScope}
              data-testid="expand-scope-button"
              className="text-xs"
            >
              <Maximize2 className="w-3.5 h-3.5 mr-1.5" />
              Expand to all products
            </Button>
          )}
        </div>

        {/* Result count */}
        <p className="text-sm text-muted-foreground mt-2" data-testid="catalog-results-count">
          {loading ? 'Loading...' : error ? 'Results unavailable' : `${resultCount || 0} ${countUnit === 'models' ? 'models' : 'product families'}`}
        </p>
      </div>
    </div>
  );
};
